import React from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { FolderItem, ExpandIcon, ContentWrapper, FolderName } from '../FolderTreeItem/styles';

import { SettingsNavNode } from './types';
import { TreeContainer, LeafLink } from './styles';

const containsSelection = (node: SettingsNavNode): boolean => {
  if (node.isSelected) return true;
  return (node.children ?? []).some(containsSelection);
};

const NavItem: React.FC<{ node: SettingsNavNode; depth: number }> = ({ node, depth }) => {
  const hasChildren = !!node.children && node.children.length > 0;
  const holdsSelection = containsSelection(node);
  const [expanded, setExpanded] = React.useState(node.defaultExpanded ?? holdsSelection);

  React.useEffect(() => {
    if (holdsSelection) setExpanded(true);
  }, [holdsSelection]);

  if (!hasChildren) {
    return (
      <LeafLink to={node.to ?? '#'} id={`settings-nav-${node.key}`} aria-current={node.isSelected ? 'page' : undefined}>
        <FolderItem $selected={!!node.isSelected} $depth={depth}>
          <ExpandIcon $visible={false} $expanded={false} />
          <ContentWrapper>
            <FolderName $selected={!!node.isSelected}>{node.label}</FolderName>
          </ContentWrapper>
        </FolderItem>
      </LeafLink>
    );
  }

  const toggle = (): void => setExpanded((prev) => !prev);
  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggle();
    }
  };

  return (
    <>
      <FolderItem
        $selected={false}
        $depth={depth}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        <ExpandIcon $visible $expanded={expanded}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </ExpandIcon>
        <ContentWrapper>
          <FolderName $selected={false}>{node.label}</FolderName>
        </ContentWrapper>
      </FolderItem>
      {expanded && node.children?.map((child) => <NavItem key={child.key} node={child} depth={depth + 1} />)}
    </>
  );
};

const SettingsNavTree: React.FC<{ nodes: SettingsNavNode[] }> = ({ nodes }) => (
  <TreeContainer>
    {nodes.map((node) => (
      <NavItem key={node.key} node={node} depth={0} />
    ))}
  </TreeContainer>
);

export default SettingsNavTree;
